import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { RadialDiagram } from "@/components/RadialDiagram";
import { HowSupportBegins, WhereSupportHappens } from "@/components/SupportJourney";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";

const heroLine = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const CASCADE = [
  "Difficulty at school",
  "Suspension",
  "Kinship carer repeatedly leaves employment",
  "Employment and financial pressure",
  "Stress within the home",
  "Placement pressure",
  "Child experiences further rejection & disconnection",
  "Behaviour escalates",
  "More services and workers become involved",
];

const QUESTIONS = [
  "What is happening around this child?",
  "What knowledge does this child already hold?",
  "What has the problem convinced other people to believe about them?",
  "What relationships and community can we strengthen around them?",
  "How can we stop this problem from taking over other parts of their life?",
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-cream grain px-6 pt-32 pb-20">
        <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] bg-blush/45 organic-blob blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-ochre/20 organic-blob-alt blur-3xl" />
        <div className="relative max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-16 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-terracotta mb-8"
              data-testid="hero-kicker"
            >
              Aboriginal-owned · Narrative therapy · Mentoring · Movement · Community
            </motion.p>
            <h1 className="font-display font-light tracking-tight text-charcoal leading-[1.02] text-5xl sm:text-6xl lg:text-7xl">
              {["Healing through", "story.", "Strength through", "connection."].map((l, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span custom={i} variants={heroLine} initial="hidden" animate="show" className={`block ${i === 3 ? "italic text-terracotta" : ""}`}>
                    {l}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="mt-8 font-display italic text-2xl sm:text-3xl text-earth"
              data-testid="hero-tagline"
            >
              Every family needs an extra Aunty for a yarn.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="mt-5 text-base lg:text-lg leading-relaxed text-earth/90 max-w-xl"
            >
              Aboriginal-owned narrative therapy, mentoring, movement and community support walking alongside
              children, young people, families and carers when life and systems become overwhelming.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link data-testid="hero-request-support-btn" to="/request-support" className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105">
                Request Support <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link data-testid="hero-partner-btn" to="/partner" className="inline-flex items-center gap-2 rounded-full border-2 border-charcoal px-8 py-[14px] text-sm font-bold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-cream">
                Partner With Ngawilyi
              </Link>
              <Link data-testid="hero-shape-btn" to="/consultation" className="text-sm font-bold uppercase tracking-widest text-earth underline decoration-ochre decoration-2 underline-offset-8 hover:text-terracotta transition-colors">
                Help Shape Ngawilyi
              </Link>
            </motion.div>
          </motion.div>
          <motion.div style={{ y: imgY }} className="hidden lg:block lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="organic-blob overflow-hidden shadow-[0_40px_90px_rgba(74,55,40,0.25)] animate-float-slow">
                <img
                  src="https://images.unsplash.com/photo-1644426358812-879f02d1d867?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHx3YXJtJTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwZ3JhZGllbnR8ZW58MHx8fHwxNzg3NTUzNDI3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Warm abstract texture — placeholder for community photography"
                  className="w-full h-[520px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-8 -left-10 rounded-[32px] bg-charcoal text-cream px-8 py-6 shadow-[0_24px_60px_rgba(45,42,38,0.35)]">
                <p className="font-display italic text-2xl leading-snug">"Someone who can yarn.<br />Someone who can listen."</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-earth/60"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Scroll</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </motion.div>
      </section>

      <Marquee items={["Yarn.", "Move.", "Connect.", "Create.", "Belong."]} />

      {/* ONE RELATIONSHIP */}
      <section className="relative px-6 py-24 sm:py-36 bg-cream overflow-hidden">
        <div className="absolute top-20 -right-40 w-[30rem] h-[30rem] bg-sunset/15 organic-blob blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal>
              <Kicker>The Ngawilyi model</Kicker>
              <h2 className="font-display font-light tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                One relationship.<br /><span className="italic text-terracotta">Multiple ways of responding.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
                Too often, a child experiencing overlapping challenges ends up with an education worker, a mentor,
                a placement worker, a contact worker, a substance-use service, a behaviour program — another
                appointment, another assessment, another person asking them to tell their story again.
              </p>
              <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
                Ngawilyi asks: <em className="font-display text-xl">what if we didn't need ten workers?</em> What if one
                trusted Aunty could walk beside the child and family, drawing from a range of program models
                depending on what was happening in their life?
              </p>
              <p className="mt-5 text-base lg:text-lg leading-relaxed font-semibold text-charcoal">
                One relationship. Multiple life domains. Less system overwhelm.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <Link data-testid="home-explore-programs-btn" to="/programs" className="group mt-10 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-terracotta">
                Explore Programs <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <RadialDiagram />
          </Reveal>
        </div>
      </section>

      <HowSupportBegins />
      <WhereSupportHappens />

      {/* CASCADE */}
      <section className="relative bg-charcoal text-cream px-6 py-24 sm:py-36 overflow-hidden grain">
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-terracotta/25 organic-blob blur-3xl" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker light>When behaviour starts affecting everything</Kicker>
              <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08]">
                One difficulty can <span className="italic text-ochre">cascade</span> across a child's whole life.
              </h2>
              <p className="mt-8 text-cream/75 leading-relaxed">
                Ngawilyi works within these intersections — before one difficulty is allowed to dominate every
                other area of a child's life.
              </p>
              <p className="mt-8 text-xs uppercase tracking-[0.25em] font-bold text-ochre">Instead of asking "What is wrong with this child?" we ask —</p>
              <ul className="mt-6 space-y-4">
                {QUESTIONS.map((q, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <li className="flex gap-4 items-start">
                      <span className="font-display italic text-terracotta text-xl leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-cream/85 leading-relaxed">{q}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-[40px] bg-cream/[0.06] border border-cream/10 p-8 sm:p-12 backdrop-blur-sm">
              {CASCADE.map((step, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className="flex items-center gap-5">
                    <span className="shrink-0 h-10 w-10 rounded-full bg-terracotta/20 border border-terracotta/40 flex items-center justify-center font-display italic text-ochre">
                      {i + 1}
                    </span>
                    <p className={`py-4 ${i === CASCADE.length - 1 ? "text-ochre font-semibold" : "text-cream/85"}`}>{step}</p>
                  </div>
                  {i < CASCADE.length - 1 && <div className="ml-5 h-4 w-px bg-gradient-to-b from-terracotta/50 to-transparent" />}
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE HOLDERS */}
      <section className="relative px-6 py-24 sm:py-36 bg-blush/40 overflow-hidden grain">
        <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] bg-terracotta/20 organic-blob-alt blur-3xl" />
        <div className="relative max-w-5xl mx-auto text-left">
          <Reveal>
            <Kicker>Children as knowledge holders</Kicker>
            <blockquote className="font-display font-light text-3xl sm:text-5xl lg:text-6xl leading-[1.12] text-charcoal">
              "You are not just someone receiving help.{" "}
              <span className="italic text-terracotta">You have knowledge that could help someone else.</span>"
            </blockquote>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 text-base lg:text-lg leading-relaxed text-earth max-w-2xl">
              Young people experiencing difficult circumstances develop significant knowledge about survival,
              relationships, systems, identity and what actually helps. A young person's experience should never
              only become another case note — their knowledge may become something capable of helping another
              young person.
            </p>
            <Link data-testid="home-threads-btn" to="/threads" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-terracotta">
              Finding the Threads <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHEN SCHOOL FALLS APART */}
      <section className="px-6 py-24 sm:py-36 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <Kicker>Practical Aunty support</Kicker>
            <h2 className="font-display font-light tracking-tight text-charcoal text-4xl sm:text-5xl leading-[1.08]">
              When school falls apart, <span className="italic text-terracotta">support happens where life happens.</span>
            </h2>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-earth">
              A young person has been suspended. Their kinship carer receives another call asking them to leave
              work and collect the child. Repeated suspensions begin threatening the carer's employment — and the
              resulting stress can affect the entire household and placement.
            </p>
            <p className="mt-5 text-base lg:text-lg leading-relaxed text-earth">
              Where appropriate, safe, authorised and within the service model, a Ngawilyi Aunty — a narrative
              therapist — may step in to support the young person following suspension. But this is not simply transport or babysitting.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-[40px] bg-creamdark border border-ochre/30 p-10 sm:p-12 shadow-[0_20px_40px_rgba(74,55,40,0.06)]">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-6">That afternoon can become</p>
              <div className="flex flex-wrap gap-3">
                {["a yarn", "a walk", "a gym session", "lunch together", "narrative work", "education advocacy", "letter writing", "a shared wisdom letter", "planning how tomorrow could look different"].map((t) => (
                  <span key={t} className="rounded-full bg-cream border border-ochre/40 px-5 py-2.5 text-sm font-semibold text-earth">{t}</span>
                ))}
              </div>
              <p className="mt-8 text-sm leading-relaxed text-earth/80">
                The immediate pressure on the carer is reduced while the young person remains meaningfully
                supported. That is the Ngawilyi difference.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROGRAMS PREVIEW */}
      <section className="px-6 pb-24 sm:pb-36 bg-cream">
        <div className="max-w-7xl mx-auto">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <Kicker>Programs</Kicker>
              <h2 className="font-display font-light tracking-tight text-charcoal text-4xl sm:text-5xl">Proposed programs, <span className="italic text-terracotta">shaped with community.</span></h2>
            </div>
            <Link data-testid="home-all-programs-btn" to="/programs" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-terracotta">
              All Programs <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Those Who Know, Not the Adults That Don't", d: "Placement stability through the knowledge of children who have lived it.", tone: "bg-terracotta text-cream", tag: "In Development" },
              { n: "02", t: "Turning Story", d: "Substance use, identity and narrative mentoring — never defined by the problem.", tone: "bg-creamdark text-charcoal", tag: "In Development" },
              { n: "03", t: "Education Outside the Box", d: "Education engagement that begins with relationship, identity and belonging.", tone: "bg-blush/50 text-charcoal", tag: "In Development" },
              { n: "05", t: "Therapy, Mentoring & Movement", d: "Sometimes it's easier to yarn when you're walking side-by-side.", tone: "bg-ochre/30 text-charcoal", tag: "Taking EOI" },
            ].map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <Link to="/programs" data-testid={`home-program-card-${p.n}`} className={`group block rounded-[36px] p-8 h-full transition-transform duration-500 hover:-translate-y-2 ${p.tone}`}>
                  <span className="font-display italic text-lg opacity-60">{p.n}</span>
                  <h3 className="mt-4 font-display text-2xl leading-snug">{p.t}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${p.tone.includes("text-cream") ? "text-cream/80" : "text-earth/80"}`}>{p.d}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-70">
                    <Tag tone={p.tone.includes("text-cream") ? "blush" : "terracotta"}>{p.tag}</Tag>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Ngawilyi isn't another worker in a child's life. It's an Aunty beside them when they need one."
        text="We are seeking partners, referrers, community voices and pilot funders to help shape what comes next."
        primary={{ to: "/partner", label: "Partner With Ngawilyi" }}
        secondary={{ to: "/consultation", label: "Help Shape Ngawilyi" }}
      />
    </div>
  );
};

export default Home;
